/**
 * The child half of the `postMessage` link. RETIRE-WITH-PRICECALCULATOR.
 *
 * Park-independent: it is handed the origins it may talk to. Everything about
 * WHAT to say lives in `PostMessageResults`; this file is only the pipe, plus
 * the two pieces of sequencing that are easy to get wrong.
 *
 * ---------------------------------------------------------------------------
 * 1. THE FIRST `objs` IS ALWAYS LOST, SO WE HAVE TO ASK
 * ---------------------------------------------------------------------------
 *
 * The parent posts `state` and `objs` from its own `DOMContentLoaded` handler,
 * to `iframe.contentWindow`. It creates the iframe in that same handler, a few
 * statements earlier, so at the moment it posts, `contentWindow` is the
 * `about:blank` document the browser gives a fresh frame — our document does not
 * exist yet and the message goes nowhere. There is no retry on the parent side.
 *
 * `list` exists for exactly this: it makes the parent re-send both. So the child
 * must ask, and must keep asking for a short while, because the parent installs
 * its `message` listener inside that same `DOMContentLoaded` handler and a
 * `list` that arrives before then is also dropped. Hence the backoff.
 *
 * Verified against the captured parent: `list` produces `objs` unconditionally,
 * and `state` first but only when the host page's `history.state` is truthy.
 *
 * ---------------------------------------------------------------------------
 * 2. `targetOrigin` IS NOT THE SAME IN BOTH DIRECTIONS
 * ---------------------------------------------------------------------------
 *
 * `compat-protocol.md` says `targetOrigin` is `https://prijs.devergarde.nl` in
 * both directions. That is right for the parent, whose target IS us, and wrong
 * for us: posting to `window.parent` with our own origin as `targetOrigin` means
 * the browser compares it against the PARENT's origin, finds `www.devergarde.nl`
 * instead, and drops the message with a console error the park never sees.
 *
 * Our `targetOrigin` is the host page's origin. We learn it from `objs` — which
 * is the other reason that message carries `encodeURIComponent(location.origin)`
 * — and until then we use the configured list, one post per candidate. Not `'*'`:
 * `'*'` would hand the message to whoever framed us.
 */

import {
  encodeChild,
  parseParent,
  type ParentMessage,
} from './protocol.js';

export interface BridgeOptions {
  /** Origins the host page may have. See `vergarde.ts`. */
  hostOrigins: readonly string[];
  /** The window to post to. Injected in tests; `window.parent` in the browser. */
  parent?: Pick<Window, 'postMessage'>;
  /** The window to listen on. Injected in tests; `window` in the browser. */
  self?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  /** Which accommodations the page is about, and where it is. */
  onObjs?: (objs: { origin: string; search: string; keys: string[] }) => void;
  /** The host page's `history.state`. */
  onState?: (state: unknown) => void;
  /** Focus left the iframe. */
  onBlur?: () => void;
  /** Milliseconds between `list` attempts, until `objs` arrives. */
  listBackoff?: readonly number[];
  /** Nothing answered. The park is not selling and nobody can see it. */
  onSilence?: () => void;
  warn?: (message: string) => void;
}

export interface Bridge {
  /** Send one message. Throws rather than sending something undeliverable. */
  send(target: string, command: string, argument?: string): void;
  /** The host origin, once `objs` has told us. */
  hostOrigin(): string | null;
  /** The keys on the page, or null until `objs` arrives. */
  keys(): readonly string[] | null;
  destroy(): void;
}

/**
 * Four tries over roughly a second and a half. Long enough to cover a parent
 * whose `DOMContentLoaded` is late behind a tag manager, short enough that the
 * silence callback fires while somebody might still be looking.
 */
const DEFAULT_BACKOFF = [0, 100, 400, 1000] as const;

export function createBridge(options: BridgeOptions): Bridge {
  const warn = options.warn ?? ((m: string) => { console.warn(m); });
  const parent = options.parent ?? (typeof window === 'undefined' ? null : window.parent);
  const listener = options.self ?? (typeof window === 'undefined' ? null : window);
  const backoff = options.listBackoff ?? DEFAULT_BACKOFF;

  let host: string | null = null;
  let keys: readonly string[] | null = null;
  let destroyed = false;
  const timers: ReturnType<typeof setTimeout>[] = [];

  const onMessage = (event: MessageEvent) => {
    // The only check the parent makes is on the origin, and it is the only check
    // worth making here too: the message is a string of ours, and a page that
    // framed us cannot forge its own origin.
    if (!options.hostOrigins.includes(event.origin)) return;
    const message: ParentMessage | null = parseParent(event.data);
    if (message === null) return;

    if (message.kind === 'objs') {
      host = event.origin;
      keys = message.keys;
      for (const t of timers) clearTimeout(t);
      timers.length = 0;
      options.onObjs?.(message);
      return;
    }
    if (message.kind === 'state') { options.onState?.(message.state); return; }
    options.onBlur?.();
  };

  listener?.addEventListener('message', onMessage as EventListener);

  const send: Bridge['send'] = (target, command, argument) => {
    /*
      THE PARENT CAN STOP BEING A WINDOW WHILE A TIMER IS STILL PENDING.

      `destroyed || parent === null` was not enough. This bridge retries `list`
      on a backoff, so a timer can fire after the frame it lives in has gone —
      and a torn-down window is not null, it is an object whose `postMessage`
      has been taken away. The call then throws out of a timer callback, where
      nothing is waiting to catch it.

      In a browser that is a stray console error in a frame already closing. In
      CI it is an uncaught exception that turns a wholly green run non-zero at
      random, which is worse than the bug: it teaches everyone to read the
      summary line instead of the exit code.

      So the guard asks whether the thing can still be posted to, rather than
      whether it exists.
    */
    if (destroyed || typeof parent?.postMessage !== 'function') return;
    const message = encodeChild(target, command, argument);
    // Once `objs` has arrived we know exactly one origin; before that, every
    // candidate. A candidate that is not the real parent silently discards it,
    // which is what we want.
    for (const origin of host === null ? options.hostOrigins : [host]) {
      parent.postMessage(message, origin);
    }
  };

  // Ask, and keep asking until answered.
  backoff.forEach((delay, i) => {
    timers.push(setTimeout(() => {
      if (destroyed || keys !== null) return;
      send('all', 'list');
      if (i === backoff.length - 1) {
        timers.push(setTimeout(() => {
          if (destroyed || keys !== null) return;
          warn(
            'compat-2027: the host page never answered `list`, so we do not know which '
            + 'accommodations it is about. Every tile stays hidden and the page looks '
            + 'exactly like its own initial state — nobody will notice from the outside.',
          );
          options.onSilence?.();
        }, 2000));
      }
    }, delay));
  });

  return {
    send,
    hostOrigin: () => host,
    keys: () => keys,
    destroy() {
      destroyed = true;
      for (const t of timers) clearTimeout(t);
      timers.length = 0;
      listener?.removeEventListener('message', onMessage as EventListener);
    },
  };
}
